// Item and upgrade cards. One builder serves three uses:
//  - compact (~340 px) in-world comparison beside item pickups (GameScene, world space)
//  - full (~420×560) cards in the Reward / Station overlays
//  - major-upgrade cards after a boss
// Content comes from describeItem/describeUpgrade (loot); if those are not
// available yet the card falls back to the item data tables / the instance.

import Phaser from 'phaser';
import { COLORS, css, mix, rarityColor } from '../config/palette';
import type { ItemComparison, ItemDisplay, ItemInstance, MajorUpgradeDef } from '../core/types';
import { describeItem, describeUpgrade } from '../systems/loot/describe';
import { fallbackItemDisplay, fallbackUpgradeDisplay, itemName, synergyColor, synergyName } from './format';
import {
  HintBar,
  TYPE,
  chamferPoints,
  createChip,
  createTagChip,
  drawBrackets,
  drawCornerTicks,
  fitText,
  flowLayout,
  glowTexture,
  iconTexture,
  iconTint,
  txt,
  type UiAction,
} from './kit';

export interface ItemCardOptions {
  /** Comparison against the equipped item (deltas, tags, synergy changes). */
  comparison?: ItemComparison | null;
  /** Small in-world card shown beside a pickup. */
  compact?: boolean;
  width?: number;
  /** Footer with input hints, e.g. [E] EQUIP  [X] SALVAGE +12. */
  controls?: { equip?: string; salvage?: string };
  selected?: boolean;
  /** Minimum height (to align a row of cards). */
  minHeight?: number;
  /** Pre-computed display (skips describeItem). */
  display?: ItemDisplay;
}

export interface UpgradeCardOptions {
  selected?: boolean;
  width?: number;
  minHeight?: number;
  compact?: boolean;
  /** Footer hint, e.g. [ENTER] INSTALL. */
  controls?: { confirm?: string };
  /** Pre-computed display (skips describeUpgrade). */
  display?: ItemDisplay;
}

/** Runtime controls stored on the card container (see getCardHandle). */
export interface ItemCardHandle {
  readonly width: number;
  readonly height: number;
  readonly color: number;
  setSelected(on: boolean): void;
  /** Grow the card to at least h (bottom-anchored footer moves down). */
  setHeight(h: number): void;
  setDimmed(on: boolean): void;
}

export const CARD_FULL_W = 420;
export const CARD_FULL_H = 560;
export const CARD_COMPACT_W = 340;

/** Card controller for a container returned by createItemCard / createUpgradeCard. */
export function getCardHandle(card: Phaser.GameObjects.Container): ItemCardHandle | null {
  return (card.getData('card') as ItemCardHandle | undefined) ?? null;
}

/** Item card container (origin at its centre). Works in GameScene (world space) and overlay scenes. */
export function createItemCard(scene: Phaser.Scene, item: ItemInstance, opts: ItemCardOptions = {}): Phaser.GameObjects.Container {
  let display: ItemDisplay;
  try {
    display = opts.display ?? describeItem(item);
    if (!display || !display.name) throw new Error('empty display');
  } catch {
    display = fallbackItemDisplay(item);
  }
  const hints: { action: UiAction; label: string }[] = [];
  if (opts.controls?.equip) hints.push({ action: 'interact', label: opts.controls.equip });
  if (opts.controls?.salvage) hints.push({ action: 'salvage', label: opts.controls.salvage });
  return buildCard(scene, display, {
    compact: !!opts.compact,
    width: opts.width,
    minHeight: opts.minHeight,
    selected: !!opts.selected,
    comparison: opts.comparison ?? null,
    hints,
  });
}

/** Major-upgrade card (boss reward). Origin at its centre. */
export function createUpgradeCard(scene: Phaser.Scene, def: MajorUpgradeDef, opts: UpgradeCardOptions = {}): Phaser.GameObjects.Container {
  let display: ItemDisplay;
  try {
    display = opts.display ?? describeUpgrade(def);
    if (!display || !display.name) throw new Error('empty display');
  } catch {
    display = fallbackUpgradeDisplay(def);
  }
  const hints: { action: UiAction; label: string }[] = [];
  if (opts.controls?.confirm) hints.push({ action: 'confirm', label: opts.controls.confirm });
  return buildCard(scene, display, {
    compact: !!opts.compact,
    width: opts.width,
    minHeight: opts.minHeight,
    selected: !!opts.selected,
    comparison: null,
    hints,
    upgrade: def,
  });
}

// ───────────────────────────── Builder ─────────────────────────────

interface BuildOptions {
  compact: boolean;
  width?: number;
  minHeight?: number;
  selected: boolean;
  comparison: ItemComparison | null;
  hints: { action: UiAction; label: string }[];
  upgrade?: MajorUpgradeDef;
}

const WARN = COLORS.threatHot;

function displayName(item: ItemInstance | null): string {
  if (!item) return '';
  try {
    return describeItem(item).name;
  } catch {
    return itemName(item);
  }
}

function buildCard(scene: Phaser.Scene, d: ItemDisplay, o: BuildOptions): Phaser.GameObjects.Container {
  const compact = o.compact;
  const W = o.width ?? (compact ? CARD_COMPACT_W : CARD_FULL_W);
  const pad = compact ? 18 : 24;
  const inner = W - pad * 2;
  const color = d.color ?? rarityColor(d.rarity);
  const bright = mix(color, 0xffffff, 0.35);
  const bodySize = TYPE.small; // 22 px everywhere: readable at 1080p
  const card = scene.add.container(0, 0);
  // Looping tweens (icon float, glow pulse) die with the card.
  const loops: Phaser.Tweens.Tween[] = [];
  card.once(Phaser.GameObjects.Events.DESTROY, () => loops.forEach((tw) => tw.remove()));
  const frame = scene.add.graphics();
  const content = scene.add.container(0, 0);
  card.add([frame, content]);

  // ── Header strip: rarity + kind ──
  const headerH = compact ? 34 : 40;
  const headerG = scene.add.graphics();
  content.add(headerG);
  const rar = txt(scene, pad + 16, headerH / 2, d.rarityLabel.toUpperCase(), { font: 'display', size: compact ? 17 : 18, weight: 700, color: bright, spacing: compact ? 2 : 3, originY: 0.5 });
  const kind = txt(scene, W - pad, headerH / 2, d.kindLabel.toUpperCase(), { font: 'display', size: compact ? 16 : 17, weight: 600, color: COLORS.uiDim, spacing: compact ? 1 : 2, originX: 1, originY: 0.5 });
  // Keep both labels on one line: drop the redundant suffix, then shrink, then truncate.
  const room = (): number => kind.x - (rar.x + rar.width + 14);
  if (kind.width > room()) kind.setText(d.kindLabel.toUpperCase().replace(/\s+MODULE$/, '').replace(/^.*\/\/\s*/, (m) => (compact ? '' : m)));
  if (kind.width > room()) kind.setFontSize(compact ? 14 : 15);
  fitText(kind, Math.max(40, room()));
  content.add([rar, kind]);
  headerG.fillStyle(color, 1);
  headerG.fillPoints(chamferPoints(pad, headerH / 2 - 5, 10, 10, { tl: 5, br: 5 }), true);

  let y = headerH + (compact ? 12 : 14);

  // ── Icon ──
  const iconKeyRes = iconTexture(scene, d.icon.key);
  const exact = iconKeyRes === d.icon.key;
  let nameX = pad;
  let nameW = inner;
  const isWeaponArt = /^weapon_/.test(d.icon.key);
  if (!compact) {
    const wellH = 138;
    const well = scene.add.graphics();
    well.fillStyle(0x05080c, 0.75);
    well.fillPoints(chamferPoints(pad, y, inner, wellH, { tl: 10, br: 10 }), true);
    // Fine grid inside the well.
    well.lineStyle(1, color, 0.07);
    for (let gx = pad + 18; gx < pad + inner; gx += 18) well.lineBetween(gx, y + 2, gx, y + wellH - 2);
    for (let gy = y + 18; gy < y + wellH; gy += 18) well.lineBetween(pad + 2, gy, pad + inner - 2, gy);
    well.lineStyle(1, color, 0.35);
    well.strokePoints(chamferPoints(pad, y, inner, wellH, { tl: 10, br: 10 }), true, true);
    content.add(well);
    const glow = scene.add.image(W / 2, y + wellH / 2, glowTexture(scene)).setTint(color).setBlendMode(Phaser.BlendModes.ADD);
    glow.setDisplaySize(inner * 0.9, wellH * 1.1).setAlpha(d.rarity === 'prototype' ? 0.42 : d.rarity === 'advanced' ? 0.3 : 0.18);
    content.add(glow);
    if (d.rarity !== 'standard') {
      loops.push(scene.tweens.add({ targets: glow, alpha: glow.alpha * 0.55, duration: 1400, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' }));
    }
    const icon = scene.add.image(W / 2, y + wellH / 2, iconKeyRes, exact ? d.icon.frame : undefined);
    const fw = icon.frame.width || 1;
    const fh = icon.frame.height || 1;
    const boxW = isWeaponArt ? inner - 50 : 100;
    const boxH = isWeaponArt ? wellH - 40 : 100;
    icon.setScale(Math.min(boxW / fw, boxH / fh, isWeaponArt ? 3 : 4) * (d.icon.scale ?? 1));
    const tint = d.icon.tint ?? (iconTint(scene, iconKeyRes, color) !== 0xffffff ? bright : undefined);
    if (tint !== undefined) icon.setTint(tint);
    content.add(icon);
    loops.push(scene.tweens.add({ targets: icon, y: icon.y - 4, duration: 1800, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' }));
    y += wellH + 16;
  } else {
    const box = 64;
    const g = scene.add.graphics();
    g.fillStyle(0x05080c, 0.8);
    g.fillPoints(chamferPoints(pad, y, box, box, { tl: 8, br: 8 }), true);
    g.lineStyle(1, color, 0.5);
    g.strokePoints(chamferPoints(pad, y, box, box, { tl: 8, br: 8 }), true, true);
    content.add(g);
    const icon = scene.add.image(pad + box / 2, y + box / 2, iconKeyRes, exact ? d.icon.frame : undefined);
    const fw = icon.frame.width || 1;
    const fh = icon.frame.height || 1;
    icon.setScale(Math.min((box - 10) / fw, (box - 10) / fh) * (d.icon.scale ?? 1));
    const tint = d.icon.tint ?? (iconTint(scene, iconKeyRes, color) !== 0xffffff ? bright : undefined);
    if (tint !== undefined) icon.setTint(tint);
    content.add(icon);
    nameX = pad + box + 14;
    nameW = inner - box - 14;
  }

  // ── Name + identity ──
  const nameStartY = y;
  const name = txt(scene, nameX, y, d.name.toUpperCase(), {
    font: 'display',
    size: compact ? 25 : 32,
    weight: 700,
    color: 0xffffff,
    wrap: nameW,
    spacing: 1,
    lineSpacing: -4,
  });
  if (name.height > (compact ? 64 : 84)) name.setFontSize(compact ? 21 : 27);
  content.add(name);
  y += name.height + 2;
  if (d.short) {
    const short = txt(scene, nameX, y, d.short, { size: bodySize, color: bright, wrap: nameW });
    content.add(short);
    y += short.height;
  }
  if (compact) y = Math.max(y, nameStartY + 64);
  y += compact ? 10 : 12;

  // ── Divider ──
  const div = scene.add.graphics();
  div.lineStyle(1, color, 0.35);
  div.lineBetween(pad, y, W - pad, y);
  content.add(div);
  y += compact ? 10 : 14;

  // ── Effect lines ──
  const maxLines = compact ? 3 : 6;
  const lines = (d.lines ?? []).filter(Boolean).slice(0, maxLines);
  for (const line of lines) {
    const bullet = scene.add.graphics();
    bullet.fillStyle(color, 1);
    bullet.fillPoints(chamferPoints(pad + 1, y + 9, 7, 7, { tl: 3.5, br: 3.5 }), true);
    const t = txt(scene, pad + 18, y, line, { size: bodySize, color: COLORS.uiText, wrap: inner - 18, lineSpacing: -1 });
    content.add([bullet, t]);
    y += t.height + 4;
  }

  // ── Tradeoff (warning colour) ──
  if (d.tradeoff) {
    y += 2;
    const warn = scene.add.graphics();
    warn.fillStyle(WARN, 1);
    warn.fillTriangle(pad, y + 19, pad + 13, y + 19, pad + 6.5, y + 6);
    warn.fillStyle(COLORS.ink, 1);
    warn.fillRect(pad + 5.75, y + 10, 1.5, 5);
    warn.fillRect(pad + 5.75, y + 16, 1.5, 1.5);
    const t = txt(scene, pad + 22, y, d.tradeoff, { size: bodySize, color: WARN, wrap: inner - 22, lineSpacing: -1 });
    content.add([warn, t]);
    y += t.height + 4;
  }

  // ── Tags ──
  if (d.tags?.length) {
    y += 6;
    const chips = d.tags.slice(0, compact ? 4 : 6).map((tag) => createTagChip(scene, 0, 0, tag, { size: 'sm' }));
    content.add(chips);
    y += flowLayout(chips, pad, y, inner, 6, 6) + 4;
  }

  // ── Mech restriction (upgrades), unless the header already says so ──
  if (o.upgrade?.mech && !`${d.rarityLabel} ${d.kindLabel}`.toUpperCase().includes(o.upgrade.mech.toUpperCase())) {
    y += 6;
    const chip = createChip(scene, pad, 0, `${o.upgrade.mech} only`, COLORS[o.upgrade.mech], { size: 'sm', filled: true });
    chip.setY(y + chip.height / 2);
    content.add(chip);
    y += chip.height + 4;
  }

  // ── Comparison ──
  const cmp = o.comparison;
  if (cmp) {
    y += compact ? 8 : 12;
    const boxTop = y;
    const cmpG = scene.add.graphics();
    content.add(cmpG);
    y += 10;
    let headText: string;
    let headColor: number;
    if (cmp.autoEquip) {
      headText = cmp.item.kind === 'mod' ? 'FREE MOD SLOT – AUTO-INSTALL' : 'EMPTY SLOT – AUTO-EQUIP';
      headColor = COLORS.uiGood;
    } else if (cmp.current) {
      headText = `REPLACES: ${displayName(cmp.current).toUpperCase()}`;
      headColor = COLORS.uiText;
    } else {
      headText = 'NEW ADDITION';
      headColor = COLORS.uiGood;
    }
    const head = txt(scene, pad + 12, y, headText, { font: 'display', size: compact ? 17 : 18, weight: 700, color: headColor, spacing: 1.5, wrap: inner - 24 });
    content.add(head);
    y += head.height + 6;
    const deltas = cmp.deltas.slice(0, compact ? 4 : 6);
    for (const dl of deltas) {
      const col = dl.better === true ? COLORS.uiGood : dl.better === false ? COLORS.uiBad : COLORS.uiDim;
      const label = txt(scene, pad + 12, y, dl.label, { size: bodySize, color: COLORS.uiDim });
      const value = txt(scene, W - pad - 34, y + 2, dl.value, { font: 'display', size: compact ? 18 : 19, weight: 600, color: col, originX: 1 });
      if (label.width + value.width > inner - 50) label.setFontSize(19);
      const arrow = scene.add.graphics();
      const ax = W - pad - 20;
      const ay = y + 13;
      arrow.fillStyle(col, 1);
      if (dl.better === true) arrow.fillTriangle(ax - 7, ay + 5, ax + 7, ay + 5, ax, ay - 6);
      else if (dl.better === false) arrow.fillTriangle(ax - 7, ay - 5, ax + 7, ay - 5, ax, ay + 6);
      else arrow.fillRect(ax - 6, ay - 1.5, 12, 3);
      content.add([label, value, arrow]);
      y += Math.max(label.height, 26);
    }
    if (!deltas.length && !cmp.autoEquip && cmp.current) {
      const t = txt(scene, pad + 12, y, 'Sidegrade – no stat changes', { size: bodySize, color: COLORS.uiDim });
      content.add(t);
      y += t.height;
    }
    // Synergy changes in their own colours.
    const syn: Phaser.GameObjects.Container[] = [];
    for (const id of cmp.synergiesGained) syn.push(createChip(scene, 0, 0, synergyName(id), synergyColor(id), { size: 'sm', prefix: '+ ', filled: true }));
    for (const id of cmp.synergiesLost) syn.push(createChip(scene, 0, 0, synergyName(id), COLORS.uiBad, { size: 'sm', prefix: '− ', dim: true }));
    if (cmp.gainedTags.length && !compact) {
      for (const tag of cmp.gainedTags.slice(0, 3)) syn.push(createTagChip(scene, 0, 0, tag, { size: 'sm', prefix: '+' }));
    }
    if (syn.length) {
      y += 6;
      content.add(syn);
      y += flowLayout(syn, pad + 12, y, inner - 24, 6, 6);
    }
    y += 12;
    cmpG.fillStyle(0x05080c, 0.6);
    cmpG.fillPoints(chamferPoints(pad, boxTop, inner, y - boxTop, { tl: 8, br: 8 }), true);
    cmpG.lineStyle(1, cmp.autoEquip ? COLORS.uiGood : COLORS.uiBorder, cmp.autoEquip ? 0.6 : 1);
    cmpG.strokePoints(chamferPoints(pad, boxTop, inner, y - boxTop, { tl: 8, br: 8 }), true, true);
  }

  // ── Footer controls (bottom-anchored) ──
  let footer: HintBar | null = null;
  const footerH = o.hints.length ? (compact ? 50 : 58) : 0;
  if (o.hints.length) {
    footer = new HintBar(scene, W / 2, 0, o.hints, { align: 'center', size: compact ? 26 : 28, gap: compact ? 22 : 30, fontSize: compact ? 17 : 19 });
    content.add(footer);
  }
  const contentH = y + (compact ? 14 : 18) + footerH;
  let H = Math.max(contentH, o.minHeight ?? (compact ? 0 : CARD_FULL_H));

  // ── Frame (redrawn on select / resize) ──
  let selected = o.selected;
  const ch = compact ? 14 : 18;
  const drawFrame = (): void => {
    const x0 = -W / 2;
    const y0 = -H / 2;
    frame.clear();
    // Outer glow (stronger when selected).
    const layers = selected ? 4 : 2;
    for (let i = layers; i >= 1; i--) {
      const dd = i * (selected ? 3 : 2.5);
      frame.lineStyle(3, color, (selected ? 0.5 : 0.22) / (i * 1.4));
      frame.strokePoints(chamferPoints(x0 - dd, y0 - dd, W + dd * 2, H + dd * 2, { tl: ch + dd * 0.41, br: ch + dd * 0.41 }), true, true);
    }
    frame.fillStyle(mix(COLORS.uiBg, color, selected ? 0.07 : 0.035), 0.96);
    frame.fillPoints(chamferPoints(x0, y0, W, H, { tl: ch, br: ch }), true);
    // Header strip tint.
    frame.fillStyle(color, selected ? 0.24 : 0.15);
    frame.fillPoints(chamferPoints(x0, y0, W, headerH, { tl: ch }), true);
    frame.lineStyle(1, color, 0.5);
    frame.lineBetween(x0 + 1, y0 + headerH, x0 + W - 1, y0 + headerH);
    if (footerH) {
      frame.fillStyle(0x000000, 0.25);
      frame.fillPoints(chamferPoints(x0, y0 + H - footerH, W, footerH, { br: ch }), true);
      frame.lineStyle(1, COLORS.uiBorder, 0.9);
      frame.lineBetween(x0 + 1, y0 + H - footerH, x0 + W - 1, y0 + H - footerH);
    }
    frame.lineStyle(selected ? 2.5 : 1.5, selected ? mix(color, 0xffffff, 0.3) : color, selected ? 1 : 0.85);
    frame.strokePoints(chamferPoints(x0, y0, W, H, { tl: ch, br: ch }), true, true);
    drawCornerTicks(frame, x0, y0, W, H, { tl: ch, br: ch }, mix(color, 0xffffff, 0.5), selected ? 1 : 0.7);
    if (selected) drawBrackets(frame, x0, y0, W, H, 0xffffff, 22, 12, 2, 0.9);
    content.setPosition(x0, y0);
    footer?.setY(H - footerH / 2);
  };
  drawFrame();
  card.setSize(W, H);

  const handle: ItemCardHandle = {
    get width() {
      return W;
    },
    get height() {
      return H;
    },
    color,
    setSelected(on: boolean) {
      if (on === selected) return;
      selected = on;
      drawFrame();
    },
    setHeight(h: number) {
      const nh = Math.max(contentH, h);
      if (nh === H) return;
      H = nh;
      card.setSize(W, H);
      drawFrame();
    },
    setDimmed(on: boolean) {
      content.setAlpha(on ? 0.45 : 1);
      frame.setAlpha(on ? 0.6 : 1);
    },
  };
  card.setData('card', handle);
  return card;
}

/** Text colour helper for callers that print the rarity of an item. */
export function rarityCss(item: ItemInstance): string {
  return css(rarityColor(item.rarity));
}
